import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Moon, Sun } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { AMB } from '../../lib/ambiances';
import { useAmbiance } from '../Ambiance/AmbianceContext';

/** Logo configuré par le super admin (Paramètres > Général), sinon le wordmark recadré. */
export const useBrandLogo = () => {
    const [logo, setLogo] = useState('/logo-liamani-wordmark.png');
    useEffect(() => {
        adminApi.getGlobalSettings().then((data: any) => { if (data?.loginLogo) setLogo(data.loginLogo); }).catch(() => {});
    }, []);
    return logo;
};

export const BrandLogo: React.FC<{ className?: string }> = ({ className = 'h-6' }) => {
    const logo = useBrandLogo();
    return <img src={logo} alt="L'IAmani" className={`${className} w-auto max-w-[200px] object-contain`} />;
};

/** « ● Ambiance automne » — la petite ligne qui nomme la saison en cours au-dessus du titre. */
export const SeasonLine: React.FC<{ className?: string }> = ({ className = '' }) => {
    const { active, tokens } = useAmbiance();
    return (
        <div className={`inline-flex items-center gap-2 ${className}`}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: tokens.acc }} />
            <span className="text-[12px] font-semibold uppercase tracking-[.1em] text-content-muted">Ambiance {AMB[active].label}</span>
        </div>
    );
};

/** Sur-titre de section : point d'accent + capitales espacées. */
export const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { tokens } = useAmbiance();
    return (
        <div className="inline-flex items-center gap-2 mb-3">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: tokens.acc }} />
            <span className="text-[10px] font-bold uppercase tracking-[.09em]" style={{ color: tokens.acc }}>{children}</span>
        </div>
    );
};

type PillProps = {
    variant?: 'primary' | 'secondary' | 'ghost';
    size?: 'md' | 'lg';
    to?: string;
    href?: string;
    onClick?: () => void;
    type?: 'button' | 'submit';
    // La page de connexion doit pouvoir bloquer l'envoi le temps d'une verification : la prop etait
    // acceptee par les appelants mais jamais transmise au bouton, si bien qu'elle ne faisait rien.
    disabled?: boolean;
    className?: string;
    children: React.ReactNode;
};

const PILL: Record<NonNullable<PillProps['variant']>, string> = {
    primary: 'bg-content text-surface hover:-translate-y-px',
    secondary: 'bg-surface text-content border border-border hover:border-content',
    ghost: 'text-content hover:opacity-70',
};

/** Boutons en pilule de la vitrine (passation §3) : une seule action pleine par rangée. */
export const Pill: React.FC<PillProps> = ({ variant = 'primary', size = 'md', to, href, onClick, type = 'button', disabled = false, className = '', children }) => {
    const cls = `inline-flex items-center justify-center rounded-full font-semibold whitespace-nowrap shrink-0 transition-all ${size === 'lg' ? 'h-[46px] px-6 text-[15px]' : 'h-[38px] px-[18px] text-[14px]'} ${PILL[variant]} ${className}`;
    if (to) return <Link to={to} className={cls}>{children}</Link>;
    if (href) return <a href={href} className={cls}>{children}</a>;
    return <button type={type} onClick={onClick} disabled={disabled} className={cls}>{children}</button>;
};

/** Interrupteur jour / sombre façon iOS (38 × 22, pastille 16) pour les visiteurs. */
export const ThemeSwitch: React.FC<{ isDark: boolean; onToggle: () => void; className?: string }> = ({ isDark, onToggle, className = '' }) => (
    <button
        type="button"
        role="switch"
        aria-checked={isDark}
        aria-label={isDark ? 'Passer en thème jour' : 'Passer en thème sombre'}
        title={isDark ? 'Thème jour' : 'Thème sombre'}
        onClick={onToggle}
        className={`inline-flex items-center gap-2 shrink-0 ${className}`}
    >
        <Sun size={13} className={isDark ? 'text-content-muted' : 'text-content'} />
        <span className={`relative w-[38px] h-[22px] rounded-full transition-colors ${isDark ? 'bg-content' : 'bg-surface-soft border border-border'}`}>
            <span className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full shadow-sm transition-all ${isDark ? 'left-[19px] bg-surface' : 'left-[2px] bg-white'}`} />
        </span>
        <Moon size={13} className={isDark ? 'text-content' : 'text-content-muted'} />
    </button>
);
